import { useMutation } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import { renewQuoteMutation } from 'Services';
import { enqueueSnackbar } from 'notistack';
import { getQuoteDetails } from '@setvi/shared/services/react-query';
import { useQuoteCache } from './use-quote-cache';

interface RenewQuoteParams {
  validUntil: Date | Moment;
  refreshMethod?: 'KEEP_PRICING' | 'RESET_STANDARD' | 'PRESERVE_MARGINS';
}

/**
 * Hook to renew/extend a quote's expiration date
 * Updates the quote cache on success
 */
export const useRenewQuote = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);

  return useMutation({
    ...renewQuoteMutation(),
    mutationFn: async ({
      validUntil,
      refreshMethod = 'KEEP_PRICING'
    }: RenewQuoteParams) => {
      const date = moment.isMoment(validUntil)
        ? validUntil.startOf('day').toDate()
        : validUntil;

      const response = await renewQuoteMutation().mutationFn({
        quoteId,
        refreshMethod,
        validUntil: date
      });

      return response;
    },

    onSuccess: async () => {
      // Refetch quote and update cache with fresh data
      const freshQuote = await cache.queryClient.fetchQuery(
        getQuoteDetails(quoteId)
      );

      if (freshQuote) {
        cache.setQuote(() => freshQuote);
      }

      enqueueSnackbar('Quote extended successfully', {
        variant: 'success'
      });
    },

    onError: () => {
      enqueueSnackbar('Failed to extend quote', {
        variant: 'error'
      });
    }
  });
};
